import { useState, useEffect, useRef } from 'react';
import ChevronUpIcon from '../../assets/icons/chevron-down.svg?react';
import ChevronDownIcon from '../../assets/icons/chevron-down.svg?react';
import './TimeInput.css';

/**
 * TimeInput 컴포넌트 (커스텀 드롭다운 time picker)
 * @param {Object} props
 * @param {string} props.value - 시간 값 (HH:mm 형식)
 * @param {Function} props.onChange - 값 변경 시 호출 (시간 문자열을 인자로 받음)
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {string} props.className - 추가 CSS 클래스
 * @param {string} props.label - 라벨 텍스트
 * @param {boolean} props.required - 필수 여부
 */
const TimeInput = ({
  value = '',
  onChange = () => {},
  disabled = false,
  className = '',
  label = '',
  required = false,
}) => {
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [isHourOpen, setIsHourOpen] = useState(false);
  const [isMinuteOpen, setIsMinuteOpen] = useState(false);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);
  const hourContainerRef = useRef(null);
  const minuteContainerRef = useRef(null);

  // value prop이 변경되면 시간과 분 파싱
  useEffect(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':').map(Number);
      setHour(h);
      setMinute(m);
    }
  }, [value]);

  // 시간 배열 생성 (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);
  // 분 배열 생성 (0-59)
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleHourChange = (newHour) => {
    if (disabled) return;
    setHour(newHour);
    onChange(`${String(newHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    setIsHourOpen(false); // 숫자 선택 시 드롭다운 닫기
  };

  const handleMinuteChange = (newMinute) => {
    if (disabled) return;
    setMinute(newMinute);
    onChange(`${String(hour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`);
    setIsMinuteOpen(false); // 숫자 선택 시 드롭다운 닫기
  };

  const handleHourUp = (e) => {
    e.stopPropagation();
    if (hour > 0) {
      setHour(hour - 1);
      onChange(`${String(hour - 1).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  };

  const handleHourDown = (e) => {
    e.stopPropagation();
    if (hour < 23) {
      setHour(hour + 1);
      onChange(`${String(hour + 1).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  };

  const handleMinuteUp = (e) => {
    e.stopPropagation();
    if (minute > 0) {
      setMinute(minute - 1);
      onChange(`${String(hour).padStart(2, '0')}:${String(minute - 1).padStart(2, '0')}`);
    }
  };

  const handleMinuteDown = (e) => {
    e.stopPropagation();
    if (minute < 59) {
      setMinute(minute + 1);
      onChange(`${String(hour).padStart(2, '0')}:${String(minute + 1).padStart(2, '0')}`);
    }
  };

  // 선택된 항목으로 스크롤 (드롭다운 열릴 때)
  useEffect(() => {
    if (isHourOpen && hourListRef.current) {
      const selectedItem = hourListRef.current.querySelector('.time-item.selected');
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }
  }, [isHourOpen, hour]);

  useEffect(() => {
    if (isMinuteOpen && minuteListRef.current) {
      const selectedItem = minuteListRef.current.querySelector('.time-item.selected');
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }
  }, [isMinuteOpen, minute]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hourContainerRef.current && !hourContainerRef.current.contains(event.target)) {
        setIsHourOpen(false);
      }
      if (minuteContainerRef.current && !minuteContainerRef.current.contains(event.target)) {
        setIsMinuteOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`time-input-wrapper ${className}`}>
      {label && (
        <label className={`time-input-label ${required ? 'required' : ''}`}>
          {label}
        </label>
      )}
      <div className={`time-picker-container ${disabled ? 'disabled' : ''}`}>
        {/* 시간 선택 */}
        <div className="time-column" ref={hourContainerRef}>
          <div 
            className={`time-display ${isHourOpen ? 'open' : ''}`}
            onClick={() => !disabled && setIsHourOpen(!isHourOpen)}
          >
            {String(hour).padStart(2, '0')}
          </div>
          
          {isHourOpen && (
            <div className="time-dropdown">
              <button
                className="chevron-btn chevron-up"
                onClick={handleHourUp}
                disabled={disabled || hour <= 0}
              >
                <ChevronUpIcon style={{ transform: 'rotate(180deg)' }} />
              </button>
              <div className="time-list" ref={hourListRef}>
                {hours.map((h) => (
                  <div
                    key={h}
                    className={`time-item ${h === hour ? 'selected' : ''}`}
                    onClick={() => handleHourChange(h)}
                  >
                    {String(h).padStart(2, '0')}
                  </div>
                ))}
              </div>
              <button
                className="chevron-btn chevron-down"
                onClick={handleHourDown}
                disabled={disabled || hour >= 23}
              >
                <ChevronDownIcon />
              </button>
            </div>
          )}
        </div>

        {/* 구분자 */}
        <div className="time-separator">:</div>

        {/* 분 선택 */}
        <div className="time-column" ref={minuteContainerRef}>
          <div 
            className={`time-display ${isMinuteOpen ? 'open' : ''}`}
            onClick={() => !disabled && setIsMinuteOpen(!isMinuteOpen)}
          >
            {String(minute).padStart(2, '0')}
          </div>
          
          {isMinuteOpen && (
            <div className="time-dropdown">
              <button
                className="chevron-btn chevron-up"
                onClick={handleMinuteUp}
                disabled={disabled || minute <= 0}
              >
                <ChevronUpIcon style={{ transform: 'rotate(180deg)' }} />
              </button>
              <div className="time-list" ref={minuteListRef}>
                {minutes.map((m) => (
                  <div
                    key={m}
                    className={`time-item ${m === minute ? 'selected' : ''}`}
                    onClick={() => handleMinuteChange(m)}
                  >
                    {String(m).padStart(2, '0')}
                  </div>
                ))}
              </div>
              <button
                className="chevron-btn chevron-down"
                onClick={handleMinuteDown}
                disabled={disabled || minute >= 59}
              >
                <ChevronDownIcon />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeInput;
